/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, CreateNodeGroupSchema, NodeGroupResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type CreateNodeGroupMutationConfig = {
  onSuccess?: (data: NodeGroupResponse, variables: CreateNodeGroupSchema) => void;
  onError?: (error: unknown, variables: CreateNodeGroupSchema) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for creating a new node group.
 *
 * @param config.onSuccess - Called after successful node group creation
 * @param config.onError - Called if creation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for creating node groups
 */
export function useCreateNodeGroupMutation(config: CreateNodeGroupMutationConfig) {
  const queryClient = useQueryClient();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isError, error, isPending } = useMutation<
    NodeGroupResponse,
    ApiError,
    CreateNodeGroupSchema
  >({
    mutationFn: (nodeGroup: CreateNodeGroupSchema) => {
      return mgmtPlaneAPIClient.nodeGroups.createNodeGroup({
        tenantId: tenantId!,
        requestBody: nodeGroup,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nodeGroups'] });
      config?.onSuccess?.(data, variables);
    },
    onError: config?.onError,
    onSettled: config?.onSettled,
  });

  return { mutate, mutateAsync, isError, error, isPending };
}
